import React, { useEffect, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
    Search, TrendingUp, TrendingDown, Minus, Newspaper,
    RefreshCw, Flag, Globe, Clock, LineChart, X, FileText,
} from "lucide-react";
import { fetchNewsFeed, fetchPublicAds, fetchPublicSettings } from "@/lib/api";
import NewsCard from "@/components/NewsCard";
import { AdSlot, BottomStickyAd } from "@/components/AdSlot";
import GlobalMarkets from "@/components/GlobalMarkets";
import BrandLogo from "@/components/BrandLogo";
import { TelegramIcon, TwitterIcon, LinkedInIcon, InstagramIcon } from "@/components/SocialIcons";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import PromoBanner from "@/components/PromoBanner";
import PageSEO from "@/components/PageSEO";
import MoodMeter from "@/components/MoodMeter";
import NewsletterForm from "@/components/NewsletterForm";
import InstallAppButton from "@/components/InstallAppButton";
import { ReportButtons } from "@/components/ReportButtons";
import { StockSuggestInput } from "@/components/StockSuggestInput";

const REFRESH_MS = 60000;
const IN_FEED_EVERY_N = 6;

const SENTIMENT_TABS = [
    { key: "all", label: "All", Icon: Newspaper },
    { key: "positive", label: "Positive", Icon: TrendingUp },
    { key: "negative", label: "Negative", Icon: TrendingDown },
    { key: "neutral", label: "Neutral", Icon: Minus },
];

// India Standard Time clock — displays "13 JUL · 10:14:07 PM IST" (12-hour)
// Visible on every screen size, compact on mobile.
const LiveClock = () => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    const parts = new Intl.DateTimeFormat("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit", month: "short",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        hour12: true,
    }).formatToParts(now);
    const day = parts.find((p) => p.type === "day")?.value;
    const month = parts.find((p) => p.type === "month")?.value?.toUpperCase();
    const hour = parts.find((p) => p.type === "hour")?.value;
    const minute = parts.find((p) => p.type === "minute")?.value;
    const second = parts.find((p) => p.type === "second")?.value;
    const ampm = parts.find((p) => p.type === "dayPeriod")?.value?.toUpperCase();
    return (
        <div className="flex items-center gap-1 font-mono text-[10px] sm:text-[11px] text-black/60 tabular-nums shrink-0 leading-none" data-testid="live-clock">
            <Clock className="w-3 h-3 text-black/40" />
            <span>{day} {month}</span>
            <span className="font-bold text-black/80">{hour}:{minute}<span className="hidden sm:inline text-black/40">:{second}</span> {ampm}</span>
            <span className="hidden md:inline text-black/40">IST</span>
        </div>
    );
};

const SectionHeader = ({ Icon, kicker, title, subtitle, testId }) => (
    <div className="flex items-end justify-between gap-4 flex-wrap mb-6 pb-4 border-b border-black/12" data-testid={testId}>
        <div className="max-w-3xl min-w-0">
            <div className="eyebrow flex items-center gap-2">
                <Icon className="w-3.5 h-3.5" />
                <span className="truncate">{kicker}</span>
            </div>
            <h2 className="section-title text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-3 break-words">{title}</h2>
            {subtitle && (
                <p className="mt-3 font-body text-sm text-black/60 max-w-2xl leading-relaxed">
                    {subtitle}
                </p>
            )}
        </div>
    </div>
);

const NewsGrid = ({ items, isLoading, prefix, inFeedAds = [] }) => {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`${prefix}-loading`}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={`skeleton-${prefix}-${i}`} className="news-card overflow-hidden animate-pulse">
                        <div className="aspect-[16/9] bg-white/5" />
                        <div className="p-4 space-y-2">
                            <div className="h-3 w-24 bg-white/10" />
                            <div className="h-5 w-full bg-white/10" />
                            <div className="h-5 w-3/4 bg-white/10" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    if (items.length === 0) {
        return (
            <div className="news-card p-10 text-center" data-testid={`${prefix}-empty`}>
                <Newspaper className="w-8 h-8 mx-auto text-white/30 mb-3" />
                <div className="text-white/70 text-sm">No news matches your filters.</div>
            </div>
        );
    }
    const featured = items[0];
    const rest = items.slice(1);
    // Interleave in-feed ads every N items (only if ads exist)
    const withAds = [];
    rest.forEach((item, i) => {
        withAds.push({ kind: "item", item, idx: i });
        if (inFeedAds.length && (i + 1) % IN_FEED_EVERY_N === 0) {
            withAds.push({ kind: "ad", idx: i });
        }
    });
    return (
        <div className="space-y-6" data-testid={`${prefix}-grid`}>
            <NewsCard item={featured} featured testId={`${prefix}-featured`} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {withAds.map((entry, i) =>
                    entry.kind === "item" ? (
                        <NewsCard key={entry.item.id} item={entry.item} testId={`${prefix}-item-${entry.idx}`} />
                    ) : (
                        <AdSlot
                            key={`ad-${i}`}
                            slot="in_feed"
                            ads={inFeedAds}
                            testId={`${prefix}-infeed-ad-${i}`}
                            className="col-span-1"
                            minHeight={280}
                        />
                    ),
                )}
            </div>
        </div>
    );
};

const LoadMoreButton = ({ items, total, isFetching, onLoadMore, testId }) => {
    // Only surface the button when there are more stories in the 24h window
    // beyond what's already rendered. Backend caps limit at 500.
    const hasMore = items.length < total && items.length < 500;
    if (!hasMore) return null;
    return (
        <div className="mt-8 flex justify-center">
            <button
                onClick={onLoadMore}
                disabled={isFetching}
                data-testid={testId}
                className="font-sub text-[11px] uppercase tracking-[0.2em] font-bold bg-black text-white px-6 py-3 rounded-sm hover:bg-[#0FA95A] transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center gap-2"
            >
                {isFetching ? (
                    <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        Loading…
                    </>
                ) : (
                    "Load more stories"
                )}
            </button>
        </div>
    );
};

export default function NewsHome() {
    const [sentimentFilter, setSentimentFilter] = useState("all");
    const [regionFilter, setRegionFilter] = useState("all"); // "all" | "india" | "global"
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    // Progressive-disclosure page sizes — start with 60 stories per region and
    // let the reader expand to the full rolling-24h window on demand.
    const [indiaLimit, setIndiaLimit] = useState(60);
    const [globalLimit, setGlobalLimit] = useState(60);

    useEffect(() => {
        // Empty query auto-clears results so pressing Backspace to remove
        // the search term resets the feed. Non-empty queries wait for the
        // user to click the Search button (or press Enter) — no on-type
        // debounce, so we don't spam the archive.
        if (!query.trim()) setDebouncedQuery("");
    }, [query]);

    const submitSearch = () => setDebouncedQuery(query.trim());

    // Reset the progressive page-size whenever the active filter set changes
    // so a new query doesn't keep an inflated limit from a previous view.
    useEffect(() => {
        setIndiaLimit(60);
        setGlobalLimit(60);
    }, [sentimentFilter, regionFilter, debouncedQuery]);

    const baseParams = useMemo(() => {
        const p = {};
        if (sentimentFilter !== "all") p.sentiment = sentimentFilter;
        if (debouncedQuery) p.q = debouncedQuery;
        return p;
    }, [sentimentFilter, debouncedQuery]);

    const indiaQ = useQuery({
        queryKey: ["news-feed", "india", baseParams, indiaLimit],
        queryFn: () => fetchNewsFeed({ ...baseParams, region: "india", limit: indiaLimit }),
        refetchInterval: REFRESH_MS,
        placeholderData: keepPreviousData,
    });
    const globalQ = useQuery({
        queryKey: ["news-feed", "global", baseParams, globalLimit],
        queryFn: () => fetchNewsFeed({ ...baseParams, region: "global", limit: globalLimit }),
        refetchInterval: REFRESH_MS,
        placeholderData: keepPreviousData,
    });
    const adsQ = useQuery({
        queryKey: ["public-ads"],
        queryFn: fetchPublicAds,
        refetchInterval: 10 * 60 * 1000,
    });
    const settingsQ = useQuery({
        queryKey: ["public-settings"],
        queryFn: fetchPublicSettings,
        refetchInterval: 5 * 60 * 1000,
    });

    const indiaItems = indiaQ.data?.items || [];
    const globalItems = globalQ.data?.items || [];
    const combinedCounts = useMemo(() => {
        const a = indiaQ.data?.counts || {};
        const b = globalQ.data?.counts || {};
        // Respect the region filter so the sentiment tabs and mood meter
        // reflect the currently-visible subset instead of the total across
        // both regions.
        if (regionFilter === "india") {
            return { positive: a.positive || 0, negative: a.negative || 0, neutral: a.neutral || 0 };
        }
        if (regionFilter === "global") {
            return { positive: b.positive || 0, negative: b.negative || 0, neutral: b.neutral || 0 };
        }
        return {
            positive: (a.positive || 0) + (b.positive || 0),
            negative: (a.negative || 0) + (b.negative || 0),
            neutral: (a.neutral || 0) + (b.neutral || 0),
        };
    }, [indiaQ.data, globalQ.data, regionFilter]);
    const isFetching = indiaQ.isFetching || globalQ.isFetching;
    const ads = adsQ.data?.ads || { top_banner: [], in_feed: [], sidebar: [], bottom_sticky: [] };
    const settings = settingsQ.data || {};

    const refreshAll = () => { indiaQ.refetch(); globalQ.refetch(); };

    return (
        <div className="bg-paper min-h-screen text-black">
            <PageSEO
                path="/"
                defaultTitle={`${settings.site_name || "Nifty News"} · Live Indian & global stock market news`}
                defaultDescription={settings.hero_subtitle || "A live, sentiment-scored stream of Indian & global stock market news."}
            />
            <PromoBanner />
            {/* Sticky header */}
            <header className="sticky top-0 z-40 bg-[rgba(244,244,240,0.9)] backdrop-blur-xl border-b border-black/10" data-testid="app-header">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-2 sm:py-3 flex items-center gap-2 sm:gap-4 flex-wrap">
                    <Link to="/" className="flex items-center shrink-0" data-testid="app-logo">
                        <BrandLogo size={32} className="sm:hidden" theme="light" customLogoUrl={settings.logo_url} />
                        <BrandLogo size={40} className="hidden sm:inline-flex" theme="light" customLogoUrl={settings.logo_url} />
                    </Link>

                    <div className="order-3 sm:order-none w-full sm:w-auto sm:flex-1 sm:max-w-2xl sm:mx-auto">
                        <div className="flex items-stretch gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 z-10 pointer-events-none" />
                                <StockSuggestInput
                                    testId="news-search"
                                    type="text"
                                    value={query}
                                    onChange={setQuery}
                                    onPick={(sym) => setDebouncedQuery(sym)}
                                    onEnter={submitSearch}
                                    placeholder="Search news · Reliance, Fed, RBI, IPO…"
                                    className="w-full bg-white border border-black/10 focus:border-[#22E578] focus:ring-2 focus:ring-[#22E578]/40 outline-none py-2 sm:py-2.5 pl-10 pr-3 text-sm font-body placeholder:text-black/30 rounded-sm"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={submitSearch}
                                disabled={!query.trim()}
                                data-testid="news-search-button"
                                aria-label="Search news"
                                className="shrink-0 inline-flex items-center gap-1.5 bg-black text-white px-3 sm:px-4 font-sub text-[11px] uppercase tracking-[0.18em] font-bold rounded-sm hover:bg-[#0FA95A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Search className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Search</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto" data-testid="live-status">
                        {settings.blog_enabled && (
                            <Link
                                to="/blog"
                                className="hidden md:inline-flex items-center gap-1.5 font-sub text-[11px] uppercase tracking-[0.16em] text-black/70 hover:text-black transition-colors"
                                data-testid="nav-blog-link"
                            >
                                <FileText className="w-3.5 h-3.5" />
                                Blog
                            </Link>
                        )}
                        <button
                            onClick={refreshAll}
                            className="font-sub text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.2em] text-black/70 hover:text-[#0FA95A] flex items-center gap-1.5 transition-colors"
                            data-testid="refresh-button"
                            aria-label="Refresh news"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
                            <span className="hidden md:inline">Refresh</span>
                        </button>
                        <LiveClock />
                        <ReportButtons />
                    </div>
                </div>
            </header>

            {/* Top banner ad — only render the wrapper when an ad exists so we
                don't leave an empty gap between the sticky header and the hero */}
            {(ads.top_banner || []).some((a) => a.active !== false) && (
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-4">
                    <AdSlot slot="top_banner" ads={ads.top_banner} testId="ad-top-banner" minHeight={96} />
                </div>
            )}

            {/* Hero title */}
            <section className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-3 sm:pt-4 pb-4">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    <div className="lg:col-span-9">
                        <div className="eyebrow" data-testid="hero-kicker">
                            {settings.hero_kicker || "Latest · Live · Sentiment-Scored"}
                        </div>
                        <h1 className="section-title text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-3 break-words" data-testid="hero-title">
                            {settings.hero_title_1 || "Every headline."} <br className="hidden sm:block" />
                            <span className="text-[#0FA95A]">{settings.hero_title_2 || "Instantly labelled."}</span>
                        </h1>
                        <p className="mt-4 font-body text-sm sm:text-base text-black/60 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
                            {settings.hero_subtitle || "A live, sentiment-scored stream of Indian & global stock market news — aggregated from 20+ free sources, refreshed every 60 seconds, coloured by market mood."}
                        </p>
                    </div>
                    <div className="lg:col-span-3">
                        <div className="border border-black/12 bg-white p-4 sm:p-5 rounded-sm" data-testid="today-mood">
                            <div className="eyebrow mb-3">Today · Mood</div>

                            {/* Radial mood gauge */}
                            <MoodMeter
                                positive={combinedCounts.positive}
                                negative={combinedCounts.negative}
                                neutral={combinedCounts.neutral}
                            />

                            {/* Per-sentiment counts row (respects region filter) */}
                            <div className="mt-4 pt-4 border-t border-black/10 grid grid-cols-3 gap-2 font-heading font-black text-lg sm:text-xl">
                                <div data-testid="mood-count-positive">
                                    <div className="text-emerald-600">{combinedCounts.positive}</div>
                                    <div className="font-sub text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-black/50 mt-1 font-normal">Positive</div>
                                </div>
                                <div data-testid="mood-count-negative">
                                    <div className="text-rose-600">{combinedCounts.negative}</div>
                                    <div className="font-sub text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-black/50 mt-1 font-normal">Negative</div>
                                </div>
                                <div data-testid="mood-count-neutral">
                                    <div className="text-amber-500">{combinedCounts.neutral}</div>
                                    <div className="font-sub text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-black/50 mt-1 font-normal">Neutral</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filter bar */}
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-6">
                <div className="flex items-center gap-2 border-y border-black/12 py-3 overflow-x-auto flex-wrap sm:flex-nowrap" data-testid="filter-tabs">
                    <div className="eyebrow shrink-0 mr-2">Sentiment</div>
                    {SENTIMENT_TABS.map(({ key, label, Icon }) => {
                        const active = sentimentFilter === key;
                        const count = key === "all"
                            ? combinedCounts.positive + combinedCounts.negative + combinedCounts.neutral
                            : combinedCounts[key] || 0;
                        return (
                            <button
                                key={key}
                                onClick={() => setSentimentFilter(key)}
                                className={`shrink-0 font-sub text-[11px] uppercase tracking-[0.16em] px-3 py-2 flex items-center gap-1.5 rounded-sm transition-colors border ${
                                    active
                                        ? "bg-[#22E578] text-black border-[#22E578]"
                                        : "bg-transparent text-black/70 border-transparent hover:bg-black/5"
                                }`}
                                data-testid={`tab-${key}`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                <span>{label}</span>
                                {debouncedQuery ? null : (
                                    <span className={`ml-1 text-[10px] ${active ? "text-black/60" : "text-black/40"}`}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}

                    <div className="hidden sm:block h-6 w-px bg-black/12 mx-2 shrink-0" aria-hidden="true" />
                    <div className="eyebrow shrink-0 mr-2 sm:ml-0 ml-0 mt-2 sm:mt-0 w-full sm:w-auto">Region</div>
                    {[
                        { key: "all",    label: "All",    Icon: Globe, count: indiaItems.length + globalItems.length },
                        { key: "india",  label: "India",  Icon: Flag,  count: indiaItems.length },
                        { key: "global", label: "Global", Icon: Globe, count: globalItems.length },
                    ].map(({ key, label, Icon, count }) => {
                        const active = regionFilter === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setRegionFilter(key)}
                                className={`shrink-0 font-sub text-[11px] uppercase tracking-[0.16em] px-3 py-2 flex items-center gap-1.5 rounded-sm transition-colors border ${
                                    active
                                        ? "bg-[#22E578] text-black border-[#22E578]"
                                        : "bg-transparent text-black/70 border-transparent hover:bg-black/5"
                                }`}
                                data-testid={`region-tab-${key}`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                <span>{label}</span>
                                {debouncedQuery ? null : (
                                    <span className={`ml-1 text-[10px] ${active ? "text-black/70" : "text-black/40"}`}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            <main className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-9 space-y-16">
                        {/* India section */}
                        {regionFilter !== "global" && (
                        <section data-testid="india-section">
                            <SectionHeader
                                Icon={Flag}
                                kicker={settings.india_kicker || "India · Domestic"}
                                title={settings.india_title || "Indian Stock Market News"}
                                subtitle={settings.india_subtitle || "Live coverage of the Indian equity market and listed companies."}
                                testId="india-header"
                            />
                            <NewsGrid items={indiaItems} isLoading={indiaQ.isLoading} prefix="india" inFeedAds={ads.in_feed} />
                            <LoadMoreButton
                                items={indiaItems}
                                total={indiaQ.data?.total || 0}
                                isFetching={indiaQ.isFetching}
                                onLoadMore={() => setIndiaLimit((n) => n + 60)}
                                testId="india-load-more"
                            />
                        </section>
                        )}

                        {/* Global section */}
                        {regionFilter !== "india" && (
                        <section data-testid="global-section">
                            <SectionHeader
                                Icon={Globe}
                                kicker={settings.global_kicker || "Global · International"}
                                title={settings.global_title || "Global Stock Market News"}
                                subtitle={settings.global_subtitle || "Live coverage of US, Europe and Asia-Pacific markets."}
                                testId="global-header"
                            />
                            <NewsGrid items={globalItems} isLoading={globalQ.isLoading} prefix="global" inFeedAds={ads.in_feed} />
                            <LoadMoreButton
                                items={globalItems}
                                total={globalQ.data?.total || 0}
                                isFetching={globalQ.isFetching}
                                onLoadMore={() => setGlobalLimit((n) => n + 60)}
                                testId="global-load-more"
                            />
                        </section>
                        )}
                    </div>

                    {/* Sidebar */}
                    <aside className="lg:col-span-3" data-testid="sidebar">
                        {/* Sticky Global Markets — desktop only. Direct child of <aside>
                            so the grid-stretched aside height defines the sticky range. */}
                        <div className="hidden lg:block lg:sticky lg:top-24 z-10" data-testid="sidebar-global-markets-sticky">
                            <GlobalMarkets fitViewport testId="global-markets-desktop" />
                        </div>
                        <div className="mt-6 space-y-6">
                            <AdSlot slot="sidebar" ads={ads.sidebar} testId="ad-sidebar" minHeight={300} />
                            {settings.telegram_join_enabled !== false && (
                            <div className="border border-black/12 bg-black text-white p-5 rounded-sm" data-testid="tg-promo">
                                <div className="flex items-center gap-2">
                                    <TelegramIcon className="w-5 h-5 text-[#22E578]" />
                                    <div className="font-sub text-[10px] uppercase tracking-[0.22em] text-white/60">Live · Telegram</div>
                                </div>
                                <div className="font-heading text-xl font-bold mt-3 leading-tight">
                                    Join our Telegram channel to get sentiment-based news analytics instantly.
                                </div>
                                {settings.telegram_join_url ? (
                                    <a
                                        href={settings.telegram_join_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 mt-4 bg-[#22E578] text-black px-4 py-2.5 font-sub text-[11px] uppercase tracking-[0.2em] font-bold rounded-sm hover:bg-white transition-colors"
                                        data-testid="tg-join-button"
                                    >
                                        <TelegramIcon className="w-4 h-4" />
                                        Join Telegram
                                    </a>
                                ) : (
                                    <div
                                        className="inline-flex items-center gap-2 mt-4 bg-white/10 text-white/60 px-4 py-2.5 font-sub text-[11px] uppercase tracking-[0.2em] font-bold rounded-sm"
                                        data-testid="tg-join-placeholder"
                                    >
                                        <TelegramIcon className="w-4 h-4" />
                                        Coming soon
                                    </div>
                                )}
                            </div>
                            )}
                        </div>
                    </aside>
                </div>

                {/* Footer */}
                <SiteFooter settings={settings} />
            </main>

            <BottomStickyAd ads={ads.bottom_sticky} />

            {/* Mobile-only floating "Markets" button — opens a bottom drawer with Global Markets */}
            <div className="lg:hidden">
                <Sheet>
                    <SheetTrigger asChild>
                        <button
                            type="button"
                            className="fixed bottom-6 left-6 z-50 flex items-center gap-2 bg-[#0F172A] text-white px-4 py-3 rounded-full shadow-[0_10px_28px_-8px_rgba(15,23,42,0.55)] hover:-translate-y-0.5 transition-all"
                            data-testid="mobile-markets-trigger"
                            aria-label="Open Global Markets"
                        >
                            <LineChart className="w-4 h-4 text-emerald-400" />
                            <span className="font-sub text-[11px] uppercase tracking-[0.18em] font-bold">Markets</span>
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        </button>
                    </SheetTrigger>
                    <SheetContent
                        side="bottom"
                        className="p-0 bg-[#0F172A] border-white/10 max-h-[85vh] rounded-t-2xl overflow-hidden [&>button.absolute]:hidden"
                        data-testid="mobile-markets-drawer"
                    >
                        {/* Prominent close-box in the top-right so mobile users
                            can dismiss the sheet with an obvious tap target. */}
                        <SheetClose
                            className="absolute right-3 top-3 z-10 w-9 h-9 flex items-center justify-center rounded-md border border-white/15 bg-white/5 text-white hover:bg-white/15 focus:outline-none transition-colors"
                            aria-label="Close markets"
                            data-testid="mobile-markets-close"
                        >
                            <X className="w-4 h-4" />
                        </SheetClose>
                        <div className="max-h-[85vh] overflow-y-auto gm-scroll">
                            <GlobalMarkets testId="global-markets-mobile" />
                        </div>
                    </SheetContent>
                </Sheet>
            </div>

            {/* Floating "Install app" prompt — appears when the browser
                emits beforeinstallprompt (Android/Chrome/Edge) or on iOS
                Safari with a manual guidance toast. Auto-hides once
                installed or dismissed for 7 days. */}
            <InstallAppButton />

            {/* Floating Join-Telegram bubble — visible on all scroll positions */}
            {settings.telegram_join_url && settings.telegram_join_enabled !== false && (
                <a
                    href={settings.telegram_join_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#22E578] text-black px-4 py-3 rounded-full shadow-[0_10px_28px_-8px_rgba(34,229,120,0.55)] hover:shadow-[0_16px_36px_-8px_rgba(34,229,120,0.75)] hover:-translate-y-0.5 transition-all"
                    data-testid="floating-tg-join"
                >
                    <TelegramIcon className="w-5 h-5" />
                    <span className="font-sub text-[11px] uppercase tracking-[0.18em] font-bold">Join Telegram</span>
                </a>
            )}
        </div>
    );
}

// ============ Footer ============
function SiteFooter({ settings }) {
    const socials = [
        { key: "telegram",  url: settings?.telegram_join_url, Icon: TelegramIcon, label: "Telegram" },
        { key: "twitter",   url: settings?.twitter_url,       Icon: TwitterIcon,  label: "Twitter / X" },
        { key: "linkedin",  url: settings?.linkedin_url,      Icon: LinkedInIcon, label: "LinkedIn" },
        { key: "instagram", url: settings?.instagram_url,     Icon: InstagramIcon, label: "Instagram" },
    ];
    const active = socials.filter((s) => !!s.url);
    const disclaimer = settings?.disclaimer || "";
    const tagline = settings?.footer_tagline || "Live, sentiment-scored Indian & global stock market news — aggregated from 20+ free sources, refreshed every 60 seconds.";
    const copyLine = settings?.footer_copyright_line || "Nifty News · Aggregated news · Sentiment · Not investment advice";
    return (
        <footer className="mt-16 sm:mt-20 border-t border-black/12 pt-8 sm:pt-10 pb-8" data-testid="footer">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-5">
                    <BrandLogo size={36} theme="light" customLogoUrl={settings?.logo_url} />
                    <div className="mt-4 font-body text-sm text-black/60 max-w-md leading-relaxed" data-testid="footer-tagline">
                        {tagline}
                    </div>
                    {active.length > 0 && (
                        <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="footer-social">
                            {active.map(({ key, url, Icon, label }) => (
                                <a
                                    key={key}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={label}
                                    title={label}
                                    className="w-10 h-10 flex items-center justify-center border border-black/15 rounded-full text-black/70 hover:text-black hover:bg-[#22E578] hover:border-[#22E578] transition-all"
                                    data-testid={`social-${key}`}
                                >
                                    <Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    )}
                    <div className="mt-8">
                        <NewsletterForm />
                    </div>
                </div>
                <div className="lg:col-span-7">
                    <div className="eyebrow mb-3">Investment Disclaimer</div>
                    <p className="font-body text-[13px] text-black/60 leading-relaxed" data-testid="disclaimer-text">
                        {disclaimer}
                    </p>
                </div>
            </div>
            <div className="mt-8 sm:mt-10 pt-6 border-t border-black/10 flex flex-wrap items-center justify-between gap-3 font-sub text-[10px] sm:text-[11px] uppercase tracking-[0.2em] sm:tracking-[0.22em] text-black/45">
                <div data-testid="footer-copy-line">{copyLine}</div>
                <div>© {new Date().getFullYear()}</div>
            </div>
        </footer>
    );
}
